<template>
  <div class="company-form">
    <el-form
      v-if="mode === 'add' || mode === 'edit'"
      ref="form"
      key="companyForm"
      label-width="auto"
      label-position="right"
      :model="form"
      :rules="rules"
    >
      <el-form-item label="公司名称" prop="name">
        <el-input
          v-model.trim="form.name"
          maxlength="40"
          show-word-limit
          placeholder="请输入公司名称"
        />
      </el-form-item>
      <el-form-item label="供应商类型" prop="category">
        <el-select
          v-model="form.category"
          placeholder="请选择"
          multiple
          :loading="categoryLoading"
        >
          <el-option
            v-for="item in category"
            :key="item.id"
            :label="item.name"
            :value="item.id"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="公司成立时间" prop="foundedTime">
        <el-date-picker
          v-model="form.foundedTime"
          type="date"
          format="yyyy-MM-dd"
          value-format="yyyy-MM-dd"
          placeholder="请选择公司成立时间"
          :picker-options="{
            disabledDate: date => +date > +new Date(new Date().toLocaleDateString())
          }"
          :clearable="false"
        />
      </el-form-item>
      <el-form-item label="纳税人识别号" prop="taxpayerNumber">
        <el-input
          v-model.trim="form.taxpayerNumber"
          maxlength="40"
          show-word-limit
          placeholder="请输入纳税人识别号，例：91440700XXXXXXXXXX"
        />
      </el-form-item>
      <el-form-item label="公司法人代表" prop="representative">
        <el-input
          v-model.trim="form.representative"
          maxlength="40"
          show-word-limit
          placeholder="请填写身份证对应的法人代表姓名"
        />
      </el-form-item>
      <el-form-item label="统一信用代码" prop="creditCode">
        <el-input
          v-model.trim="form.creditCode"
          maxlength="18"
          show-word-limit
          placeholder="请输入公司的统一信用代码，例：91440700XXXXXXXXXX"
        />
      </el-form-item>
      <el-form-item label="工商注册号" prop="registrationNumber">
        <el-input
          v-model.trim="form.registrationNumber"
          maxlength="40"
          show-word-limit
          placeholder="请输入公司的工商注册号，例：91440700XXXXXXXXXX"
        />
      </el-form-item>
      <el-form-item label="公司实缴资本" prop="paidCapital">
        <el-input
          v-model="form.paidCapital"
          show-word-limit
          placeholder="请输入公司的实缴资本"
        >
          <template slot="append">万元</template>
        </el-input>
      </el-form-item>
      <el-form-item label="公司注册资本" prop="registrationCapital">
        <el-input
          v-model="form.registrationCapital"
          show-word-limit
          placeholder="请输入公司的注册资本"
        >
          <template slot="append">万元</template>
        </el-input>
      </el-form-item>
      <el-form-item label="公司注册地址" prop="address">
        <el-input
          v-model="form.address"
          placeholder="请输入公司注册地址"
        />
      </el-form-item>
      <el-form-item label="公司经营范围" prop="scope">
        <el-input
          v-model.trim="form.scope"
          type="textarea"
          maxlength="1000"
          show-word-limit
          placeholder="请输入公司的经营范围"
          :autosize="{ minRows: 6, maxRows: 6 }"
        />
      </el-form-item>
      <el-form-item label="公司简介" prop="introduction">
        <el-input
          v-model.trim="form.introduction"
          type="textarea"
          maxlength="1000"
          show-word-limit
          placeholder="请输入公司简介"
          :autosize="{ minRows: 6, maxRows: 6 }"
        />
      </el-form-item>
      <el-form-item label="工商营业执照" prop="license">
        <div class="upload_wrapper">
          <div class="upload-image-container">
            <template v-if="form.license">
              <img class="upload-image" :src="form.license">
              <div class="upload-image-actions">
                <span class="upload-actions-delete" @click="deleteUpload">删除</span>
              </div>
            </template>
            <div v-else class="upload-default" />
          </div>
          <el-button class="choose-file-button" icon="el-icon-plus" @click="$refs.uploadInput.click()">
            选择照片
            <input
              ref="uploadInput"
              type="file"
              name="file"
              accept="image/gif, image/jpeg, image/jpg, image/png"
              hidden
              @change="uploadChange"
            >
          </el-button>
          <el-input v-show="false" v-model="form.license" class="upload-input" />
        </div>
        <span class="upload-tip">只能上传jpeg/png/gif/文件，且不超过5m</span>
      </el-form-item>
      <el-form-item label="资质附件" prop="attachment">
        <el-upload
          multiple
          :action="uploadAction"
          :file-list="fileList"
          :on-success="uploadSuccess"
          :on-remove="uploadedRemove"
          :on-error="uploadError"
        >
          <el-button type="primary">点击上传</el-button>
          <div slot="tip" class="el-upload__tip">单个文件最大不能超过1GB</div>
        </el-upload>
      </el-form-item>
      <el-form-item>
        <el-button
          class="submit"
          type="primary"
          :loading="loading"
          @click="submit"
        >{{ submitText }}</el-button>
      </el-form-item>
    </el-form>

    <el-form
      v-else
      label-width="auto"
      label-position="right"
      hide-required-asterisk
    >
      <el-form-item label="公司名称">{{ value.companyName }}</el-form-item>
      <el-form-item label="供应商类型">{{ value.userCategoryName }}</el-form-item>
      <el-form-item label="公司成立时间">{{ value.foundedTime }}</el-form-item>
      <el-form-item label="公司法人代表">{{ value.legalRepresentatives }}</el-form-item>
      <el-form-item label="统一信用代码">{{ value.creditCode }}</el-form-item>
      <el-form-item label="工商注册号">{{ value.businessRegister }}</el-form-item>
      <el-form-item label="纳税人识别号">{{ value.taxesNum }}</el-form-item>
      <el-form-item label="公司实缴资本">{{ value.reallyCapital }}</el-form-item>
      <el-form-item label="公司注册资本">{{ value.registerCapital }}</el-form-item>
      <el-form-item label="公司注册地址">{{ value.registerAddress }}</el-form-item>
      <el-form-item label="公司经营范围">{{ value.businessScope }}</el-form-item>
      <el-form-item label="公司简介">{{ value.briefing }}</el-form-item>
      <el-form-item label="工商营业执照" prop="license">
        <div class="upload_wrapper">
          <div class="upload-image-container">
            <img class="upload-image" :src="value.businessLicense">
          </div>
        </div>
      </el-form-item>
      <el-form-item label="资质附件">
        <template v-if="value.accessory">
          <div
            v-for="(item, index) in JSON.parse(value.accessory)"
            :key="index"
            class="attachment-item"
          >
            <i class="el-icon-paperclip" />
            <el-tooltip content="Top center" placement="top">
              <span>{{ item.name }}</span>

              <template slot="content">
                <a :href="item.url" :download="item.name" target="_blank">点击下载</a>
              </template>
            </el-tooltip>
          </div>
        </template>
      </el-form-item>
    </el-form>
  </div>
</template>

<script>
import { uploadFile, fetchCategory } from '@/api'

export default {
  props: {
    value: {
      type: Object,
      default() {
        return {}
      }
    },

    mode: {
      type: String,
      default: 'add'
    },

    loading: {
      type: Boolean,
      default: false
    },

    submitText: {
      type: String,
      default: '提交'
    }
  },

  data() {
    return {
      form: {
        name: '',
        category: [],
        foundedTime: '',
        taxpayerNumber: '',
        representative: '',
        creditCode: '',
        registrationNumber: '',
        paidCapital: null,
        registrationCapital: null,
        address: '',
        scope: '',
        introduction: '',
        license: '',
        attachment: []
      },

      rules: {
        name: [
          {
            required: true,
            message: '请输入公司名称',
            trigger: 'blur'
          }
        ],
        category: [
          {
            required: true,
            type: 'array',
            message: '请选择供应商类型',
            trigger: 'change'
          }
        ],
        foundedTime: [
          {
            required: true,
            message: '请选择公司成立时间',
            trigger: 'change'
          }
        ],
        taxpayerNumber: [
          {
            required: true,
            message: '请输入纳税人识别号',
            trigger: 'change'
          }
        ],
        representative: [
          {
            required: true,
            message: '请填写身份证对应的法人代表姓名',
            trigger: 'blur'
          }
        ],
        creditCode: [
          {
            required: true,
            message: '请输入公司的统一信用代码',
            trigger: 'blur'
          }
        ],
        registrationNumber: [
          {
            required: true,
            message: '请输入公司的工商注册号',
            trigger: 'blur'
          }
        ],
        paidCapital: [
          {
            required: true,
            message: '请输入公司的实缴资本',
            trigger: 'change'
          },
          {
            validator: (rule, value, callback) => {
              if (isNaN(+value)) {
                return callback(new Error('请输入正确的公司实缴资本'))
              }

              callback()
            },
            trigger: 'change'
          }
        ],
        registrationCapital: [
          {
            required: true,
            message: '请输入公司的注册资本',
            trigger: 'change'
          },
          {
            validator: (rule, value, callback) => {
              if (isNaN(+value)) {
                return callback(new Error('请输入正确的公司注册资本'))
              }

              callback()
            },
            trigger: 'change'
          }
        ],
        address: [
          {
            required: true,
            message: '请输入公司注册地址',
            trigger: 'blur'
          }
        ],
        scope: [
          {
            required: true,
            message: '请输入公司的经营范围',
            trigger: 'blur'
          }
        ],
        introduction: [
          {
            required: true,
            message: '请输入公司简介',
            trigger: 'blur'
          }
        ],
        license: [
          {
            required: true,
            message: '请上传工商营业执照',
            trigger: 'change'
          }
        ]
      },

      uploadAction: `${process.env.VUE_APP_API_URL}/api/upload/uploadAccessory`,
      fileList: [],

      category: [],
      categoryLoading: false
    }
  },

  watch: {
    mode: {
      handler(newVal) {
        if (newVal === 'add') {
          this.fetchCategory()
        }

        if (newVal === 'edit') {
          this.fetchCategory()
          this.intiFormValue()
        }
      },
      immediate: true
    }
  },

  methods: {
    /*
     * 获取分类
     */
    async fetchCategory() {
      this.categoryLoading = true

      try {
        const res = await fetchCategory()

        this.category = res.data
      } catch (error) {
        console.log('获取分类失败：', error)
      }

      this.categoryLoading = false
    },

    /*
     * 初始化表单数据
    */
    intiFormValue() {
      this.form = {
        name: this.value.companyName,
        category: this.value.userCategory || [],
        foundedTime: this.value.foundedTime,
        taxpayerNumber: this.value.taxesNum,
        representative: this.value.legalRepresentatives,
        creditCode: this.value.creditCode,
        registrationNumber: this.value.businessRegister,
        paidCapital: this.value.reallyCapital,
        registrationCapital: this.value.registerCapital,
        address: this.value.registerAddress,
        scope: this.value.businessScope,
        introduction: this.value.briefing,
        license: this.value.businessLicense,
        attachment: (this.value.accessory ? JSON.parse(this.value.accessory) : []).map(val => ({
          name: val.name,
          fileName: val.fileName,
          url: val.url
        }))
      }

      this.fileList = this.form.attachment.map(val => val)
    },

    /*
     * 上传图片
     */
    async uploadChange(e) {
      const fileData = e.target.files[0]

      if (fileData.size > 1024 * 1024 * 5) {
        this.$refs.uploadInput.value = ''

        return this.$message.error('照片最大不能超过5M')
      }

      // 使用formData打包
      const formData = new FormData()
      formData.append('file', fileData)

      try {
        const res = await uploadFile(formData)

        this.form.license = res.data.url
        this.$message.success('上传成功！')
      } catch (error) {
        this.$message.error(error.message)
      }
    },

    /*
     * 删除已上传图片
     */
    deleteUpload() {
      this.form.license = ''
    },

    /*
     * 文件大小限制
    */
    beforeAvatarUpload(file) {
      const GB = Math.pow(1024, 3)
      const sizeLimit = file.size / GB < 1

      if (!sizeLimit) {
        this.$message.error('上传文件大小不能超过 1GB!')
      }
      return sizeLimit
    },

    /*
     * 上传资质附件成功
    */
    uploadSuccess(res, file, filelist) {
      const { url, fileName } = res.data

      this.form.attachment.push({
        name: fileName,
        fileName,
        url
      })
    },

    /*
     * 上传资质附件失败
    */
    uploadError(err, file, filelist) {
      const { name = '' } = file

      this.$message.error(`${name}上传失败`)
    },

    /*
     * 删除已上传资质附件
    */
    uploadedRemove(file, filelist) {
      this.form.attachment = this.form.attachment.filter(val => {
        return val.url !== (file.response ? file.response.data.url : file.url)
      })

      this.filelist = filelist
    },

    /*
     * 提交
    */
    submit() {
      this.$refs.form.validate((valid) => {
        if (valid) {
          this.form.attachment = JSON.stringify(this.form.attachment.map(val => ({
            name: val.name,
            fileName: val.fileName,
            url: val.url
          })))
          this.$emit('submit', this.form)
        }
      })
    }
  }
}
</script>

<style lang='scss' scoped>
.company-form {
  /deep/ .el-form-item__content {
    width: 519px;
    .el-input__inner {
      height: 42px;
      line-height: 42px;
    }
  }
  /deep/ .el-date-editor {
    width: 100%;
    .el-icon-date {
      font-size: 16px;
    }
  }
  .upload_wrapper {
    padding: 10px 0 0;
    .upload-image-container {
      position: relative;
      display: inline-block;
      width: 160px;
      height: 110px;
      border-radius: 5px;
      vertical-align: top;
      overflow: hidden;
      .upload-default {
        width: 100%;
        height: 100%;
        border: 2px dashed #e8e8e8;
        background-image: url("~@/assets/images/icon-picture.png");
        background-repeat: no-repeat;
        background-size: 84px 64px;
        background-position: center center;
      }
      .upload-image {
        width: 100%;
      }
      .upload-image-actions {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        text-align: center;
        font-size: 16px;
        color: #fff;
        line-height: 110px;
        background-color: rgba(0, 0, 0, 0.5);
        opacity: 0;
        transition: opacity 0.2s ease-in;
        &:hover {
          opacity: 1;
        }
        > span {
          cursor: pointer;
        }
      }
    }
    .choose-file-button {
      width: 100px;
      height: 34px;
      color: #333;
      background-color: #e5e5e5;
      border-radius: 5px;
      padding: 0;
      margin: 0 0 0 14px;
      vertical-align: top;
    }
  }
  .upload-tip {
    font-size: 12px;
    line-height: 30px;
    color: #999;
  }
  /deep/ .el-upload__tip {
    line-height: 18px;
    color: #999;
  }
  /deep/ .el-upload-list {
    .el-upload-list__item {
      font-size: 16px;
      line-height: 44px;
      .el-progress {
        top: 30px;
      }
      .el-icon-document, .el-icon-upload-success, .el-icon-close {
        font-size: 18px;
      }
      .el-icon-document {
        margin: 0 14px 0 0;
        &::before {
          content: '\e77d'
        }
      }
      .el-icon-close {
        top: 13px;
      }
    }
  }
  .attachment-item {
    display: flex;
    align-items: center;
    line-height: 24px;
    padding: 8px 0;
    + .attachment-item {
      margin: 10px 0 0;
    }
    .el-icon-paperclip {
      font-size: 18px;
      color: #979B9C;
      margin: 0 14px 0 0;
    }
    span {
      font-size: 16px;
      &:hover {
        color: #417EF7;
      }
    }
  }
  .submit {
    width: 100%;
    height: 56px;
    border-radius: 6px;
    font-size: 18px;
    margin: 20px 0 0;
  }
}
</style>
